<script setup>
import { ref, watch, computed, onMounted } from 'vue'

const props = defineProps({
    listadosEntidadesStore: Object,
    carteleraStore: Object,
})

const filtros = ref({
    categorias: [],
    ubicaciones: [],
})

const estado = ref('todos')
const active = ref(false)
const lotes = computed(() => props.carteleraStore?.lotes?.data ?? [])
const mostrarFiltros = ref(true)
const categoriasOpts = ref([])
const ubicacionesOpts = ref([])

watch(lotes, (arr) => {
  const mapCat = new Map()
  const mapUbi = new Map()

  for (const s of arr) {
    const cat = s.tipificado?.categoria ?? null
    const catId = cat?.id
    const catNombre = cat?.nombre
    if (catId && catNombre && !mapCat.has(catId)) {
      mapCat.set(catId, { id: catId, nombre: catNombre })
    }

    const ubi = s.subasta_ubicacion ?? null
    const ubiId = ubi?.id ?? s.subasta_ubicacion_id
    const ubiNombre = ubi
      ? (ubi.direccion ? `${ubi.direccion} ${ubi.ciudad}` : `${ubi.descripcion ?? ''}${ubi.descripcion ? ' - ' : ''}${ubi.ciudad ?? ''}`.trim())
      : ''

    if (ubiId && ubiNombre && !mapUbi.has(ubiId)) {
      mapUbi.set(ubiId, { id: ubiId, nombre: ubiNombre })
    }
  }

  categoriasOpts.value = Array.from(mapCat.values())
  ubicacionesOpts.value = Array.from(mapUbi.values())
}, { immediate: true })

function toggleFiltro(tipo, valorObj) {
    const i = filtros.value[tipo].findIndex(f => f.id === valorObj.id)
    if (i === -1) filtros.value[tipo].push(valorObj)
    else filtros.value[tipo].splice(i, 1)
    enviarPayload()  
}

function obtenerLotesNoVendidos() {
  const nuevo = estado.value === 'no-vendidos' ? 'todos' : 'no-vendidos'
  estado.value = nuevo
  active.value = (nuevo === 'no-vendidos')

  const payload = armarPayload()
  payload.page = 1
  payload.per_page = props.carteleraStore.lotes.per_page

  if (estado.value === 'no-vendidos') {
    payload['estado_lote:slug'] = ['no-vendido']
  }

  props.carteleraStore.getLotes(payload)
}

function armarPayload() {
    const payload = {
        'tipificado:categoria_id': filtros.value.categorias.map(t => t.id),
        subasta_ubicacion_id: filtros.value.ubicaciones.map(u => u.id),
    }

    return payload
}

function enviarPayload() {
    const payload = armarPayload()
    payload.page = 1
    payload.per_page = props.carteleraStore?.lotes?.per_page ?? 12

    if (estado.value === 'no-vendidos') {
        payload['estado_lote:slug'] = ['no-vendido']
    }

    props.carteleraStore?.getLotes?.(payload)
}

onMounted(() => {
  filtros.value = {
    categorias: [],
    ubicaciones: [],
  }
  if (window.innerWidth < 768) {
    mostrarFiltros.value = false
  }
})
</script>


<template>

    <div @click="obtenerLotesNoVendidos" :class="[
        'btn flex-fill shadow-none w-100 text-start d-flex align-items-center justify-content-between mt-2',
        active ? 'btn-white text-black' : 'bg-soft-grey-ee text-black'
    ]" role="button" tabindex="0">
        <span>Lotes no vendidos</span>
    </div>

    <div class="shadow-none">
        <div class="card-body bg-white mt-3 rounded-2 pb-1 pt-0 pb-lg-3 pt-lg-3">
            <div class="card-header d-flex flex-wrap align-items-center gap-2 pb-0 border-0 px-0">
                <div class="d-flex justify-content-between align-items-center w-100">
                    <p class="mb-0 fs-5 d-flex align-items-center fw-blod gap-2">
                        <img src="@/assets/img/icons/layer.svg" alt=""> Filtros
                    </p>
                    <div class="d-flex align-items-center gap-3">
                        <i class="cursor-pointer d-md-none"
                            :class="mostrarFiltros ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"
                            @click="mostrarFiltros = !mostrarFiltros"></i>
                    </div>
                </div>

                <div class="w-100">
                    <div class="d-flex flex-wrap gap-2">
                        <template v-for="(valores, tipo) in filtros" :key="tipo">
                            <template v-for="valor in valores" :key="valor.id">
                                <span
                                    class="badge bg-badge-secondary text-white d-flex align-items-center gap-2 px-2 py-2 cursor-pointer"
                                    style="white-space: normal; word-break: break-word;"
                                    @click="toggleFiltro(tipo, valor)">
                                    <span>{{ valor.nombre }}</span>
                                    <i class="fa fa-times"></i>
                                </span>
                            </template>
                        </template>
                    </div>
                </div>
            </div>

            <div class="pt-0" v-if="mostrarFiltros">
                <p class="mb-2 fs-5 mt-2">Categorías</p>
                <ul class="list-unstyled">
                    <li v-for="item in categoriasOpts" :key="'ind-' + item.id" class="mb-2">
                        <a href="javascript:;" class="text-subtitulos-secondary text-decoration-none"
                            @click="toggleFiltro('categorias', item)"
                            :class="{ 'text-soft-green fw-semibold': filtros.categorias.some(f => f.id === item.id) }">
                            {{ item.nombre }}
                        </a>
                    </li>
                </ul>

                <p class="mt-3 mb-2 fs-5">Ubicación</p>
                <ul class="list-unstyled fw-bold">
                    <li v-for="item in ubicacionesOpts" :key="'ubi-' + item.id" class="mb-2">
                        <a href="javascript:;" class="text-subtitulos-secondary text-decoration-none"
                            @click="toggleFiltro('ubicaciones', item)"
                            :class="{ 'text-soft-green fw-semibold': filtros.ubicaciones.some(f => f.id === item.id) }">
                            {{ item.nombre }}
                        </a>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>


<style scoped>
a:hover {
    color: #006C4C !important;
}

.btn:hover {
    transform: none !important;
    box-shadow: none !important;
}

.card-body {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
}

.padding {
    padding-left: 0.50rem;
    padding-right: 0.50rem;
}
</style>